import Link from "next/link";
import { type ReactNode } from "react";

import { ListingStatus, type Prisma } from "@/.zenstack/logical-prisma-client";
import { generateSlug } from "@/lib/utils-client";

export type DeepLinkTarget = "place-bid" | "comments" | "photos" | "details";

export type ListingForLink = Prisma.ListingGetPayload<{
  select: {
    slug?: true;
    title: true;
    id: true;
    status: true;
  };
}>;

interface ListingLinkProps {
  listing: ListingForLink;
  children: ReactNode;
  className?: string;
  deepLinkTo?: DeepLinkTarget;
  route?: "public" | "landlord";
  commentId?: string;
  replyId?: string;
}

/**
 * Utility function to generate listing URLs with various options.
 * @param listing - The listing object with id, slug, title, and status
 * @param options - Configuration options for the URL
 * @returns The generated URL string
 */
export function generateListingUrl(
  listing: ListingForLink,
  options: {
    route?: "public" | "landlord";
    deepLinkTo?: DeepLinkTarget;
    commentId?: string;
    replyId?: string;
  } = {},
): string {
  const { route = "public", deepLinkTo, commentId, replyId } = options;

  let baseUrl: string;

  switch (route) {
    case "landlord": {
      baseUrl = `/landlord/listings/${listing.id}`;
      break;
    }
    case "public":
    default: {
      // Automatically determine if this should be a preview or public listing
      // Preview is only for DRAFT and PENDING states
      const isPreviewableState =
        listing.status === ListingStatus.DRAFT ||
        listing.status === ListingStatus.PENDING ||
        listing.status === ListingStatus.APPROVED;

      if (isPreviewableState) {
        baseUrl = `/listing/preview/${listing.id}`;
      } else {
        // Use the actual slug for public listings, or generate one if missing
        const slug = listing.slug || generateSlug(listing.title);
        baseUrl = `/listing/${slug}/${listing.id}`;
      }
      break;
    }
  }

  // Add comment/reply query parameters if provided
  const queryParams = new URLSearchParams();
  if (commentId) queryParams.set("comment", commentId);
  if (replyId) queryParams.set("reply", replyId);

  if (queryParams.toString()) {
    baseUrl = `${baseUrl}?${queryParams.toString()}`;
  }

  // Add hash fragment for deep linking
  if (deepLinkTo) {
    baseUrl = `${baseUrl}#${deepLinkTo}`;
  }

  return baseUrl;
}

export function doesSlugMatchExpectations(
  listing: ListingForLink | null | undefined,
  observedSlug: string | string[] | undefined,
): boolean {
  if (!observedSlug || !listing) return false;
  if (
    // CHORE: get this from the listing state machine
    listing.status === ListingStatus.DRAFT ||
    listing.status === ListingStatus.PENDING ||
    listing.status === ListingStatus.APPROVED
  )
    return observedSlug === "preview";
  return (
    observedSlug === listing.slug ||
    observedSlug === generateSlug(listing.title)
  );
}

/**
 * Creates a link to the listing page with support for deep linking and different routes.
 * @param listing - The listing to link to.
 * @param children - The content to display inside the link.
 * @param className - The class name to apply to the link.
 * @param deepLinkTo - Optional deep link target (e.g., "bids", "comments")
 * @param route - The route type: "public" (default), "preview", or "landlord"
 */
export function ListingLink({
  listing,
  children,
  className,
  deepLinkTo,
  route = "public",
  commentId,
  replyId,
}: ListingLinkProps) {
  const href = generateListingUrl(listing, {
    route,
    deepLinkTo,
    commentId,
    replyId,
  });

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}
